import Equipment, {
  IEquipmentApiModel,
  IEquipmentWithoutProduct,
  IPaginationResult,
} from '@models/Equipment.model';
import ProductContact, {
  IProductContact,
  IProductContactApiModel,
} from '@models/ProductContact.model';
import ProductDocuments, {
  IProductDocuments,
  IProductDocumentsApiModel,
} from '@models/ProductDocuments.model';
import ProductQa, {
  IProductQa,
  IProductQaApiModel,
} from '@models/ProductQa.model';
import ProductResources, {
  IProductResources,
  IProductResourcesApiModel,
} from '@models/ProductResources.model';
import { removeDuplicatesArray } from '@utils';
import { sortStr } from '@utils/sorting/non-ascii';
import { updateBeamUrl } from '@utils/updateBeamUrl/updateBeamUrl';
import ProductCategoryModel, {
  IProductCategory,
  IProductCategoryApiModel,
} from './ProductCategory.model';
import Manufacturer, {
  IManufacturer,
  IManufacturerApiModel,
} from './Manufacturer.model';
import Tag, { ITag, ITagApiModel, ITagRaw } from './Tag.model';

/**
 * Merges it all. Components should expect this.
 *
 * Has: IProductView model.
 * Needs: all ProductModels to include themselves as an option in the marker of its fromApiModel method and convert themselves with utility functions.
 *
 * Components will use the same options given as many Product models they want to support (usually few).
 */
export interface IProductView {
  id: string;

  imageUrl: string | null | undefined;
  model: string | null | undefined;
  manufacturerName: string | null | undefined;
  name: string | null | undefined;
}

/**
 * Last Updated: 604674d4e06964b4bea172fbcdd289795445cf25
 */
export enum ProductStatus {
  ACTIVE = 'active',
  PRESUMED_ACTIVE = 'presumed_active',
  DISCONTINUED = 'discontinued',
  SUPPORTED = 'supported',
  REPLACED = 'replaced',
  /** Default value */
  UNKNOWN = 'unknown',
}
export function cleanProductStatus(tValue: ProductStatus) {
  return tValue.replace(/[_]/, ' ');
}
const DEFAULT_MFR_ID = 'Unknown';

export interface IProduct {
  id: string;
  manufacturerId: string | undefined;
  name: string;
  status: string | null;
  model: string;
  type: string | null;
  description: string | null;
  standardWarrantyLength?: number | null;
  tempProductId?: string | null;
  createdAt: Date;
  updatedAt: Date;
  manufacturer: IManufacturer;
  resourceProducts: IProductResources[];
  documentProducts: IProductDocuments[];
  productContacts: IProductContact[];
  qaProducts: IProductQa[];
  imageUrl?: string;
  productPage?: string | null;
  hasLicense?: boolean | null;
  endOfLife?: boolean;
  hasFirmware?: boolean | null;
  standardLicenseLength?: number | null;
  equipmentsCount?: number;
  equipments?: IEquipmentWithoutProduct[];
  tags?: ITag[];
  category: null | IProductCategory;
  productStatus: ProductStatus;
}

export interface IProductApiModel {
  id: string;
  manufacturerId: string | null;
  name?: string | null;
  status: string | null;
  model?: string | null;
  type: string | null;
  description: string | null;
  standardWarrantyLength?: number | null;
  tempProductId?: string | null;
  manufacturer?: IManufacturerApiModel | [IManufacturerApiModel];
  resourceProducts?: IProductResourcesApiModel[];
  equipments?: IEquipmentApiModel[];
  documentProducts?: IProductDocumentsApiModel[];
  contactsProduct?: IProductContactApiModel[];
  qaProducts?: IProductQaApiModel[];
  createdAt: string;
  updatedAt: string;
  imageUrl?: string;
  pageUrl?: string | null;
  hasLicense?: boolean | null;
  endOfLife?: boolean;
  hasFirmware?: boolean | null;
  standardLicenseLength?: number | null;
  equipmentsCount?: number;
  tags?: ITagApiModel[];
  productStatus: ProductStatus;

  // Maybe undefined because only being sent on equipments endpoint.
  category?: null | IProductCategoryApiModel;
}
interface ITagAgg {
  f1: string;
  f2: string;
  f3: string;
  f4: string;
  f5: string;
}

export interface IProductCreateUpdate {
  id: string;
  name: string;
  model: string;
  image: string;
  manufacturerImage: string;
  equipmentsCount: number;
  tags: Array<ITagRaw>;
}

export interface IProductCreateUpdateApiModel {
  id: string;
  name: string;
  model: string;
  image: string;
  manufacturerImage: string;
  equipmentsCount: string;
  tags: Array<ITagAgg>;
}
export interface IProductApiModel2 {
  id: string | null;
  name: string | null;
  type: string | null;
  model: string | null;
  imageUrl: string | null;
  pageUrl: string | null;
  description: string | null;
}

export default class Product implements IProduct {
  public readonly id: string;

  public readonly manufacturerId: string | undefined;

  public readonly name: string;

  public readonly status: string | null;

  public readonly model: string;

  public readonly type: string | null;

  public readonly description: string | null;

  public readonly standardWarrantyLength: number | undefined | null;

  public readonly tempProductId: string | undefined | null;

  public readonly manufacturer: IManufacturer;

  public readonly resourceProducts: IProductResources[];

  public readonly documentProducts: IProductDocuments[];

  public readonly productContacts: IProductContact[];

  public readonly qaProducts: IProductQa[];

  public readonly createdAt: Date;

  public readonly updatedAt: Date;

  public readonly imageUrl: string | undefined;

  public readonly productPage?: string | null;

  public readonly hasLicense?: boolean | null;

  public readonly endOfLife?: boolean;

  public readonly hasFirmware?: boolean | null;

  public readonly standardLicenseLength?: number | null;

  public readonly equipmentsCount?: number;

  public readonly equipments?: IEquipmentWithoutProduct[];

  public readonly tags?: ITag[];

  public readonly category: IProductCategory | null;

  public readonly productStatus: ProductStatus;

  constructor(
    id: string,
    manufacturerId: string | undefined,
    name: string,
    status: string | null,
    model: string,
    type: string | null,
    description: string | null,
    createdAt: Date,
    updatedAt: Date,
    manufacturer: IManufacturer,
    resourceProducts: IProductResources[],
    documentProducts: IProductDocuments[],
    productContacts: IProductContact[],
    qaProducts: IProductQa[],
    category: IProductCategory | null,
    productStatus: ProductStatus,
    standardWarrantyLength?: number | null,
    tempProductId?: string | null,
    imageUrl?: string,
    productPage?: string | null,
    hasLicense?: boolean | null,
    endOfLife?: boolean,
    hasFirmware?: boolean | null,
    standardLicenseLength?: number | null,
    equipmentsCount?: number,
    equipments?: IEquipmentWithoutProduct[],
    tags?: ITag[],
  ) {
    this.id = id;
    this.manufacturerId = manufacturerId;
    this.name = name;
    this.status = status;
    this.model = model;
    this.type = type;
    this.description = description;
    this.manufacturer = manufacturer;
    this.resourceProducts = resourceProducts;
    this.documentProducts = documentProducts;
    this.qaProducts = qaProducts;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.standardWarrantyLength = standardWarrantyLength;
    this.tempProductId = tempProductId;
    this.imageUrl = imageUrl;
    this.productPage = productPage;
    this.hasLicense = hasLicense;
    this.endOfLife = endOfLife;
    this.hasFirmware = hasFirmware;
    this.standardLicenseLength = standardLicenseLength;
    this.productContacts = productContacts;
    this.equipmentsCount = equipmentsCount;
    this.equipments = equipments;
    this.tags = tags;
    this.category = category;
    this.productStatus = productStatus;
  }

  static fromApiModel(apiModel: IProductApiModel) {
    const {
      id,
      manufacturerId,
      status,
      type,
      description,
      standardWarrantyLength,
      tempProductId,
      pageUrl,
      hasLicense,
      endOfLife,
      hasFirmware,
      standardLicenseLength,
      equipmentsCount,
      category,
      productStatus,
    } = apiModel;

    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(Date.parse(apiModel.updatedAt));

    function getManufacturer() {
      if (Array.isArray(apiModel.manufacturer)) {
        return Manufacturer.fromApiModel(apiModel.manufacturer[0]);
      }
      if (apiModel.manufacturer) {
        return Manufacturer.fromApiModel(apiModel.manufacturer);
      }
      return {
        id: DEFAULT_MFR_ID,
        name: 'Unknown',
        status: '',
        manufacturerAddresses: [],
        manufacturerContacts: [],
        resourceManufacturers: [],
        documentManufacturers: [],
        products: [],
        productsCount: 0,
        productsContactsCount: 0,
        productsResourcesCount: 0,
        productsDocumentsCount: 0,
        manufacturerContactsCount: 0,
        manufacturerResourcesCount: 0,
        manufacturerDocumentsCount: 0,
        contactsCount: 0,
        resourcesCount: 0,
        documentsCount: 0,
        equipmentsCount: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    }
    const manufacturer: IManufacturer = getManufacturer();
    const resourceProducts = apiModel.resourceProducts
      ? ProductResources.arrayFromApiModel(apiModel.resourceProducts)
      : [];
    const documentProducts = apiModel.documentProducts
      ? ProductDocuments.arrayFromApiModel(apiModel.documentProducts)
      : [];
    const productContacts = apiModel.contactsProduct
      ? ProductContact.arrayFromApiModel(apiModel.contactsProduct)
      : [];
    const qaProducts = apiModel.qaProducts
      ? ProductQa.arrayFromApiModel(apiModel.qaProducts)
      : [];
    const equipments = apiModel.equipments
      ? Equipment.arrayWithoutProductFromApiModel(apiModel.equipments)
      : [];
    const tags = apiModel.tags ? Tag.arrayFromApiModel(apiModel.tags) : [];
    // eslint-disable-next-line no-nested-ternary
    const imageUrl = apiModel.imageUrl
      ? updateBeamUrl(apiModel.imageUrl)
      : manufacturer.imageUrl
      ? updateBeamUrl(manufacturer.imageUrl)
      : undefined;
    const name =
      apiModel.name === undefined || apiModel.name === null
        ? ''
        : apiModel.name;
    const model =
      apiModel.model === undefined || apiModel.model === null
        ? ''
        : apiModel.model;
    const categoryConverted = category
      ? ProductCategoryModel.fromApiModel(category)
      : null;
    function getManufacturerId() {
      if (manufacturerId) return manufacturerId;
      if (manufacturer.id !== DEFAULT_MFR_ID) return manufacturer.id;
      return undefined;
    }
    const product = new Product(
      id,
      getManufacturerId(),
      name,
      status,
      model,
      type,
      description,
      createdAt,
      updatedAt,
      manufacturer,
      resourceProducts,
      documentProducts,
      productContacts,
      qaProducts,
      categoryConverted,
      productStatus,
      standardWarrantyLength,
      tempProductId,
      imageUrl,
      pageUrl,
      hasLicense,
      endOfLife,
      hasFirmware,
      standardLicenseLength,
      equipmentsCount,
      equipments,
      tags,
    );

    return product;
  }

  static arrayFromApiModel(arrayApiModel: IProductApiModel[]) {
    const array = arrayApiModel.map(apiModel => Product.fromApiModel(apiModel));

    return array;
  }

  static arrayPaginatedFromApiModel(
    arrayApiModel: IPaginationResult<IProductApiModel>,
  ) {
    const array = arrayApiModel.results.map(apiModel =>
      Product.fromApiModel(apiModel),
    );

    return {
      ...arrayApiModel,
      results: array,
    };
  }

  static fromCreateUpdateApiModel(
    apiModel: IProductCreateUpdateApiModel,
  ): IProductCreateUpdate {
    const { id, image, name, model, manufacturerImage } = apiModel;

    const tagsFlat = apiModel.tags.flat(1);
    const tagsFlatWithoutNull = tagsFlat.filter(item => !!item && !!item.f1);
    const tagsMap: ITagRaw[] = tagsFlatWithoutNull.map(
      ({ f1, f2, f3, f4, f5 }) => ({
        id: f1,
        name: f2,
        color: f3,
        tcId: f4,
        tcName: f5,
      }),
    );
    const _tags = removeDuplicatesArray(tagsMap);
    const tags = Array.from(_tags).sort((a, b) => {
      return sortStr(a.name, b.name);
    });

    const equipmentsCount = Number(apiModel.equipmentsCount);

    return {
      id,
      image,
      name,
      model,
      manufacturerImage,
      tags,
      equipmentsCount,
    };
  }

  static arrayPaginatedFromCreateUpdateApiModel(
    arrayApiModel: IPaginationResult<IProductCreateUpdateApiModel>,
  ): IPaginationResult<IProductCreateUpdate> {
    const array = arrayApiModel.results.map(apiModel =>
      Product.fromCreateUpdateApiModel(apiModel),
    );

    return {
      ...arrayApiModel,
      results: array,
    };
  }

  static arrayFromCreateUpdateApiModel(
    arrayApiModel: IProductCreateUpdateApiModel[],
  ): IProductCreateUpdate[] {
    const array = arrayApiModel.map(apiModel =>
      Product.fromCreateUpdateApiModel(apiModel),
    );

    return array;
  }
}

export const ProductStatusTooltips = {
  [ProductStatus.ACTIVE]:
    'The product is being actively sold, and has active support from the manufacturer.',
  [ProductStatus.UNKNOWN]: 'The Status of the product is unknown.',
  [ProductStatus.PRESUMED_ACTIVE]:
    'This product is presumed to have active support from the manufacturer based on our data sources.',
  [ProductStatus.SUPPORTED]:
    'This product has active support from the manufacturer.',
  [ProductStatus.DISCONTINUED]:
    'This product is not supported from the manufacturer.',
  [ProductStatus.REPLACED]:
    'This product is not supported by the manufacturer, but has been replaced with another product.',
};
